import {
  AssistantRuntimeProvider,
  type ChatModelAdapter,
  type ChatModelRunResult,
  type SuggestionAdapter,
  type ThreadAssistantMessagePart,
  useLocalRuntime,
} from '@assistant-ui/react';
import type { FC, ReactNode } from 'react';
import { reportErrorToDatadog } from '../index';
import { type Recommendation, RecommendationsToolName } from './recommendations-tool';
import { JSONDecoderStream, streamAsyncIterator } from './util';

interface Props {
  readonly brainUrl: string;
  readonly children?: ReactNode;
  readonly propertyKey: string;
  readonly customerKey: string;
  readonly userId: string;
}

interface WorkingOnItResponse {
  readonly type: 'working_on_it';
  readonly working_on_it: string;
  readonly interstitial_response: boolean;
}

interface RecommendationsResponse {
  readonly type: 'recommendations';
  readonly recommendations: readonly Recommendation[];
}

interface ReplyResponse {
  readonly type: 'reply';
  readonly reply: string;
  readonly reply_cards?: readonly Recommendation[];
  readonly next_human_questions?: readonly string[];
}

type Response = WorkingOnItResponse | RecommendationsResponse | ReplyResponse;

type ContentGenerator = AsyncGenerator<ChatModelRunResult, void>;

const streamText = async function* (text: string, content: ThreadAssistantMessagePart[]): ContentGenerator {
  content.push({
    type: 'text',
    text: '',
  });
  const streamingTextIndex = content.length - 1;
  for (let i = 1; i <= text.length; ++i) {
    content[streamingTextIndex] = {
      type: 'text',
      text: text.substring(0, i),
    };
    // Add delay between chunks
    await new Promise((resolve) => setTimeout(resolve, 5));
    yield { content, status: { type: 'running' } };
  }
};

const makeParkerModelAdapter = (
  brainUrl: string,
  propertyKey: string,
  customerKey: string,
  sessionId: string,
  userId: string,
): ChatModelAdapter => ({
  run: async function* ({ messages, abortSignal: signal }): ContentGenerator {
    const query = new URLSearchParams({
      sender_id: userId,
      session_id: sessionId,
      property_key: propertyKey,
      customer_key: customerKey,
    });

    const { body, ok, status, statusText, type, url } = await fetch(`${brainUrl}/ask-ai-ui?${query.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
      }),
      signal,
    });

    const errorMessage: ThreadAssistantMessagePart = {
      type: 'text',
      text: 'I seem to be having trouble connecting to Parker. Please try again later.',
    };
    const errorResult: ChatModelRunResult = {
      content: [errorMessage],
      status: {
        type: 'incomplete',
        reason: 'error',
      },
    };
    if (!ok) {
      reportErrorToDatadog(new Error(`HTTP error! status: ${status.toString()}`), {
        status,
        statusText,
        type,
        url,
      });
      yield errorResult;
      return;
    }
    if (!body) {
      reportErrorToDatadog(new Error('No response body'), {
        status,
        statusText,
        type,
        url,
      });
      yield errorResult;
      return;
    }

    const reader = body
      .pipeThrough(new TextDecoderStream('utf-8'), { signal })
      .pipeThrough(new JSONDecoderStream<Response>(), { signal });

    const content: ThreadAssistantMessagePart[] = [];
    let hasInterstitialResponse = false;
    try {
      for await (const response of streamAsyncIterator(reader)) {
        if (hasInterstitialResponse) {
          content.pop();
        }
        switch (response.type) {
          case 'working_on_it': {
            yield* streamText(response.working_on_it, content);
            hasInterstitialResponse = response.interstitial_response;
            break;
          }
          case 'recommendations': {
            content.push({
              type: 'tool-call',
              toolName: RecommendationsToolName,
              result: { recommendations: response.recommendations },
              args: {},
              argsText: '',
              toolCallId: '12345678',
            });
            yield { content, status: { type: 'running' } };
            yield* streamText('Please give me a moment to summarize the recommendations…', content);
            hasInterstitialResponse = true;
            break;
          }
          case 'reply': {
            // Add reply cards if they exist
            if (response.reply_cards && response.reply_cards.length > 0) {
              content.push({
                type: 'tool-call',
                toolName: RecommendationsToolName,
                result: { recommendations: response.reply_cards },
                args: {},
                argsText: '',
                toolCallId: '12345678',
              });
            }
            yield { content, status: { type: 'running' } };
            yield* streamText(response.reply, content);
            yield {
              content,
              status: {
                type: 'complete',
                reason: 'stop',
              },
              metadata: {
                custom: {
                  suggestions: response.next_human_questions,
                },
              },
            };
            break;
          }
        }
      }
    } catch (error) {
      reportErrorToDatadog(error);
      yield {
        ...errorResult,
        content: [...content, errorMessage],
      };
    }
  },
});

const generateSessionId = () => Math.random().toString(36).substring(2, 15);

const makeSuggestionAdapter = (): SuggestionAdapter => ({
  generate: ({ messages }) => {
    const suggestions = messages.flatMap(({ metadata }) => {
      if ('suggestions' in metadata.custom) {
        return (metadata.custom['suggestions'] as string[]).map((prompt) => ({ prompt }));
      } else {
        return [];
      }
    });
    return Promise.resolve(suggestions);
  },
});

export const ParkerRuntimeProvider: FC<Props> = ({ brainUrl, children, propertyKey, userId, customerKey }) => {
  const runtime = useLocalRuntime(
    makeParkerModelAdapter(brainUrl, propertyKey, customerKey, generateSessionId(), userId),
    {
      adapters: {
        suggestion: makeSuggestionAdapter(),
      },
    },
  );

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
};
ParkerRuntimeProvider.displayName = 'ParkerRuntimeProvider';
